import { useRequestResetPasswordMutation } from '@/generated/graphql';
import { formatGraphQLErrors } from '@/utils/apollo-errors';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import * as yup from 'yup';

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },
}));

const RequestResetEmailSchema = yup.object().shape({
  email: yup.string().required('Required').email('Invalid Email'),
});

export interface RequestFields {
  email: string;
}

const initialValues: RequestFields = {
  email: '',
};

interface RequestResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function RequestResetPasswordDialog({
  open,
  onClose,
}: RequestResetPasswordDialogProps): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<string[]>([]);

  const [requestResetPassword, { loading }] = useRequestResetPasswordMutation();

  function handleClose() {
    setError([]);
    onClose();
  }

  async function handleSubmit(values: RequestFields) {
    if (loading) {
      return;
    }

    const input = {
      ...values,
    };

    try {
      const { data, errors } = await requestResetPassword({
        variables: input,
      });

      const response = data?.requestResetPassword;
      if (errors?.length || !response) {
        setError(
          formatGraphQLErrors(errors) ?? ['Error requesting reset password'],
        );
      } else {
        enqueueSnackbar(`Email Sent to ${input?.email}.
          Please check your email for instructions to reset your password.`);
        handleClose();
      }
    } catch (err) {
      setError(formatGraphQLErrors(err.graphQLErrors) ?? [err.message]);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="request-reset-password-dialog-title"
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
    >
      <Formik
        initialValues={initialValues}
        validationSchema={RequestResetEmailSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid }) => (
          <Form id="request-reset-password-form" className={classes.form}>
            <DialogTitle id="request-reset-password-dialog-title">
              Reset Password
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                To receive instructions to reset your password, please enter
                your email address here.
              </Box>

              {error.length > 0 &&
                error.map((err) => (
                  <Box key={err} mb={1}>
                    <Alert severity="error" variant="outlined">
                      {err}
                    </Alert>
                  </Box>
                ))}

              <Box my={2}>
                <Field
                  component={TextField}
                  name="email"
                  label="Email"
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                disabled={loading}
                color="primary"
                size="large"
              >
                Cancel
              </Button>
              <Button
                variant="text"
                color="primary"
                size="large"
                disabled={!dirty || !isValid || loading}
                type="submit"
                form="request-reset-password-form"
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
